import React from 'react';
import {
  Box,
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  useColorModeValue,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
  Avatar,
  AvatarGroup,
} from '@chakra-ui/react';
import { Shield, Activity, GitBranch, CheckCircle, AlertTriangle, Users, Brain } from 'lucide-react';

const DashboardPanel = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const governanceMetrics = [
    {
      title: "Compliance Score",
      value: "98.5%",
      change: 2.1,
      icon: Shield,
      color: "#38A169"
    },
    {
      title: "Policy Coverage",
      value: "100%",
      change: 0,
      icon: CheckCircle,
      color: "#0066CC"
    },
    {
      title: "Active Reviews",
      value: "24",
      change: -8,
      icon: Activity,
      color: "#805AD5"
    },
    {
      title: "Risk Score",
      value: "Low",
      change: -15,
      icon: AlertTriangle,
      color: "#38A169"
    }
  ];

  const teamOverview = {
    name: "Dr. Michael Thompson",
    role: "Chief Governance Officer",
    team: [
      { name: "Clinical Ethics", count: 4 },
      { name: "Data Science", count: 3 },
      { name: "Legal", count: 2 },
      { name: "Operations", count: 3 }
    ]
  };

  const integrationStatus = [
    {
      system: "Content Pipeline",
      compliance: 98,
      reviews: 12,
      status: "Compliant"
    },
    {
      system: "Neural Lab",
      compliance: 100,
      reviews: 8,
      status: "Optimized"
    },
    {
      system: "Crowdsourcing",
      compliance: 96,
      reviews: 4,
      status: "Under Review"
    }
  ];

  const recentUpdates = [
    {
      type: "policy",
      event: "Clinical data handling policy updated",
      impact: "High",
      time: "2 hours ago"
    },
    {
      type: "review",
      event: "Neural Lab compliance audit completed",
      impact: "Medium",
      time: "4 hours ago"
    },
    {
      type: "alert",
      event: "New regulatory requirement identified",
      impact: "High",
      time: "1 day ago"
    }
  ];

  return (
    <VStack spacing={6} align="stretch">
      {/* Governance Metrics */}
      <SimpleGrid columns={4} spacing={4}>
        {governanceMetrics.map((metric, index) => (
          <Box
            key={index}
            bg={bgColor}
            p={4}
            borderRadius="md"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <HStack mb={2}>
              <Icon as={metric.icon} color={metric.color} boxSize={5} />
              <Text fontSize="sm" fontWeight="medium">{metric.title}</Text>
            </HStack>
            <Stat>
              <StatNumber fontSize="2xl" color={metric.color}>{metric.value}</StatNumber>
              <StatHelpText mb={0}>
                <StatArrow type={metric.change >= 0 ? 'increase' : 'decrease'} />
                {Math.abs(metric.change)}%
              </StatHelpText>
            </Stat>
          </Box>
        ))}
      </SimpleGrid>

      {/* Team Overview */}
      <Box
        bg={bgColor}
        p={4}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <HStack spacing={4} mb={4}>
          <Avatar
            size="lg"
            name={teamOverview.name}
            src="https://bit.ly/kent-c-dodds"
          />
          <Box>
            <Text fontSize="lg" fontWeight="medium">{teamOverview.name}</Text>
            <Text fontSize="sm" color="gray.500">{teamOverview.role}</Text>
          </Box>
        </HStack>
        <SimpleGrid columns={4} spacing={4}>
          {teamOverview.team.map((dept, index) => (
            <Box key={index}>
              <Text fontSize="sm" color="gray.500">{dept.name}</Text>
              <HStack>
                <Icon as={Users} color="methodist.blue" boxSize={4} />
                <Text fontSize="sm" fontWeight="medium">{dept.count}</Text>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Integration Status */}
      <SimpleGrid columns={3} spacing={4}>
        {integrationStatus.map((system, index) => (
          <Box
            key={index}
            bg={bgColor}
            p={4}
            borderRadius="md"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <VStack align="start" spacing={3}>
              <HStack justify="space-between" width="100%">
                <Text fontSize="sm" fontWeight="medium">{system.system}</Text>
                <Badge
                  colorScheme={
                    system.status === 'Optimized' ? 'green' :
                    system.status === 'Compliant' ? 'blue' :
                    'orange'
                  }
                >
                  {system.status}
                </Badge>
              </HStack>
              <Progress
                value={system.compliance}
                size="sm"
                width="100%"
                colorScheme={system.compliance >= 98 ? 'green' : 'blue'}
              />
              <HStack justify="space-between" width="100%">
                <Text fontSize="xs" color="gray.500">Compliance: {system.compliance}%</Text>
                <Text fontSize="xs" color="gray.500">Reviews: {system.reviews}</Text>
              </HStack>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Recent Updates */}
      <Box
        bg={bgColor}
        p={4}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Text fontSize="lg" fontWeight="medium" mb={3}>Recent Updates</Text>
        <VStack spacing={3} align="stretch">
          {recentUpdates.map((update, index) => (
            <React.Fragment key={index}>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Icon
                    as={
                      update.type === 'policy' ? Shield :
                      update.type === 'review' ? CheckCircle :
                      AlertTriangle
                    }
                    color={
                      update.type === 'policy' ? 'blue.500' :
                      update.type === 'review' ? 'green.500' :
                      'orange.500'
                    }
                    boxSize={4}
                  />
                  <Text fontSize="sm">{update.event}</Text>
                </HStack>
                <HStack spacing={3}>
                  <Badge
                    colorScheme={
                      update.impact === 'High' ? 'red' :
                      update.impact === 'Medium' ? 'orange' :
                      'green'
                    }
                  >
                    {update.impact}
                  </Badge>
                  <Text fontSize="sm" color="gray.500">{update.time}</Text>
                </HStack>
              </HStack>
              {index < recentUpdates.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
};

export default DashboardPanel;
